import type { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import * as THREE from "three/webgpu";

/** Slightly thicker helper lines for empties and curves. */
export const OBJECT_HELPER_LINEWIDTH = 2;

/** Slightly thicker wire-only object strands in object mode. */
export const WIRE_OBJECT_LINEWIDTH = 2;

export function createObjectHelperLineMaterial({
  color,
  opacity,
  linewidth = OBJECT_HELPER_LINEWIDTH,
  depthTest = true,
  depthWrite = false,
  toneMapped = true,
}: {
  color: string;
  opacity: number;
  linewidth?: number;
  depthTest?: boolean;
  depthWrite?: boolean;
  toneMapped?: boolean;
}): THREE.Line2NodeMaterial {
  const material = new THREE.Line2NodeMaterial({
    color: new THREE.Color(color).getHex(),
    linewidth,
    transparent: opacity < 1,
    opacity,
    depthTest,
  });
  material.depthWrite = depthWrite;
  material.toneMapped = toneMapped;
  return material;
}

/** Expand a polyline into segment-pair positions for LineSegmentsGeometry. */
export function buildPolylineSegmentPositions(
  points: readonly (readonly [number, number, number])[],
): Float32Array {
  if (points.length < 2) return new Float32Array(0);
  const segments = new Float32Array((points.length - 1) * 6);
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const o = i * 6;
    segments[o] = a[0];
    segments[o + 1] = a[1];
    segments[o + 2] = a[2];
    segments[o + 3] = b[0];
    segments[o + 4] = b[1];
    segments[o + 5] = b[2];
  }
  return segments;
}

export interface FatLineSegmentRange {
  startSegment: number;
  segmentCount: number;
}

export interface VertexSegmentIndex {
  offsets: Uint32Array;
  segmentIndices: Uint32Array;
}

/**
 * Build a CSR reverse map from vertex index to the fat-line segments that
 * reference it.
 */
export function buildVertexSegmentIndex(
  vertexCount: number,
  segmentVertices: Uint32Array,
): VertexSegmentIndex {
  const segmentCount = segmentVertices.length / 2;
  const counts = new Uint32Array(vertexCount);
  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
    counts[segmentVertices[segmentIndex * 2]]++;
    counts[segmentVertices[segmentIndex * 2 + 1]]++;
  }
  const offsets = new Uint32Array(vertexCount + 1);
  for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
    offsets[vertexIndex + 1] = offsets[vertexIndex] + counts[vertexIndex];
  }
  const segmentIndices = new Uint32Array(offsets[vertexCount]);
  counts.fill(0);
  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
    const a = segmentVertices[segmentIndex * 2];
    const b = segmentVertices[segmentIndex * 2 + 1];
    segmentIndices[offsets[a] + counts[a]++] = segmentIndex;
    segmentIndices[offsets[b] + counts[b]++] = segmentIndex;
  }
  return { offsets, segmentIndices };
}

/** Resolve sparse contiguous fat-line upload ranges from the dirty vertex set. */
export function resolveFatLineUpdateRanges(
  segmentIndex: VertexSegmentIndex | null,
  dirtyVertices: Uint32Array | null | undefined,
): FatLineSegmentRange[] | null {
  if (!segmentIndex || !dirtyVertices || dirtyVertices.length === 0) {
    return null;
  }
  const { offsets, segmentIndices } = segmentIndex;
  const touchedSegments = new Set<number>();
  for (let i = 0; i < dirtyVertices.length; i++) {
    const vertexIndex = dirtyVertices[i]!;
    if (vertexIndex + 1 >= offsets.length) continue;
    for (
      let segmentOffset = offsets[vertexIndex];
      segmentOffset < offsets[vertexIndex + 1];
      segmentOffset++
    ) {
      touchedSegments.add(segmentIndices[segmentOffset]!);
    }
  }
  if (touchedSegments.size === 0) return [];
  const sortedSegments = [...touchedSegments].sort((a, b) => a - b);
  const ranges: FatLineSegmentRange[] = [];
  let rangeStart = sortedSegments[0]!;
  let rangeEnd = rangeStart;
  for (let i = 1; i < sortedSegments.length; i++) {
    const segmentIndexValue = sortedSegments[i]!;
    if (segmentIndexValue === rangeEnd + 1) {
      rangeEnd = segmentIndexValue;
      continue;
    }
    ranges.push({
      startSegment: rangeStart,
      segmentCount: rangeEnd - rangeStart + 1,
    });
    rangeStart = segmentIndexValue;
    rangeEnd = segmentIndexValue;
  }
  ranges.push({
    startSegment: rangeStart,
    segmentCount: rangeEnd - rangeStart + 1,
  });
  return ranges;
}

/**
 * Write segment endpoints from shared vertex positions into a fat-line buffer.
 * When ranges are provided, only those segment spans are rewritten.
 */
export function writeFatLineSegmentPositions(
  positions: Float32Array,
  segmentVertices: Uint32Array,
  vertices: Float32Array,
  ranges?: readonly FatLineSegmentRange[] | null,
  dirtyVertices?: Uint32Array | null,
  dirtyVertexPositions?: Float32Array | null,
): void {
  const segmentCount = segmentVertices.length / 2;
  const dirtyOffsets =
    dirtyVertices && dirtyVertexPositions
      ? new Map<number, number>(
          Array.from(dirtyVertices, (vertexIndex, i) => [vertexIndex, i * 3]),
        )
      : null;
  const readVertex = (
    vertexIndex: number,
    target: Float32Array,
    targetOffset: number,
  ) => {
    const dirtyOffset = dirtyOffsets?.get(vertexIndex);
    if (dirtyOffset != null && dirtyVertexPositions) {
      target[targetOffset] = dirtyVertexPositions[dirtyOffset]!;
      target[targetOffset + 1] = dirtyVertexPositions[dirtyOffset + 1]!;
      target[targetOffset + 2] = dirtyVertexPositions[dirtyOffset + 2]!;
      return;
    }
    const sourceOffset = vertexIndex * 3;
    target[targetOffset] = vertices[sourceOffset]!;
    target[targetOffset + 1] = vertices[sourceOffset + 1]!;
    target[targetOffset + 2] = vertices[sourceOffset + 2]!;
  };
  const writeSegment = (segmentIndex: number) => {
    const a = segmentVertices[segmentIndex * 2]!;
    const b = segmentVertices[segmentIndex * 2 + 1]!;
    const targetOffset = segmentIndex * 6;
    readVertex(a, positions, targetOffset);
    readVertex(b, positions, targetOffset + 3);
  };

  if (!ranges) {
    for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
      writeSegment(segmentIndex);
    }
    return;
  }
  if (ranges.length === 0) return;

  for (const range of ranges) {
    const rangeEnd = Math.min(
      segmentCount,
      range.startSegment + range.segmentCount,
    );
    for (
      let segmentIndex = range.startSegment;
      segmentIndex < rangeEnd;
      segmentIndex++
    ) {
      writeSegment(segmentIndex);
    }
  }
}

/**
 * Update a fat-line geometry in place when the segment count is unchanged.
 * Falls back to `setPositions()` when the buffer size changes.
 */
export function setFatLinePositions(
  geometry: LineSegmentsGeometry,
  positions: Float32Array,
  ranges?: readonly FatLineSegmentRange[] | null,
  refreshBounds = true,
): void {
  const startAttr = geometry.getAttribute("instanceStart") as
    | THREE.InterleavedBufferAttribute
    | undefined;
  if (!startAttr || startAttr.data.array.length !== positions.length) {
    geometry.setPositions(positions);
    return;
  }
  const target = startAttr.data.array as Float32Array;
  startAttr.data.updateRanges.length = 0;
  if (ranges) {
    if (ranges.length === 0) return;
    for (const range of ranges) {
      const start = range.startSegment * 6;
      const count = range.segmentCount * 6;
      target.set(positions.subarray(start, start + count), start);
      startAttr.data.addUpdateRange(start, count);
    }
  } else {
    target.set(positions);
  }
  startAttr.data.needsUpdate = true;
  if (refreshBounds) {
    geometry.computeBoundingSphere();
  }
}
